import { defineStore } from 'pinia'
import axios from '../config/api.js'

export const useEmailAccountsStore = defineStore('emailAccounts', {
  state: () => ({
    accounts: [],
    loading: false,
    error: null,
    oauthState: null
  }),

  getters: {
    activeAccounts: (state) => state.accounts.filter(account => account.enabled),
    connectedAccounts: (state) => state.accounts.filter(account => account.status === 'connected'),
    accountsByProvider: (state) => {
      return state.accounts.reduce((acc, account) => {
        if (!acc[account.provider]) {
          acc[account.provider] = []
        }
        acc[account.provider].push(account)
        return acc
      }, {})
    },
    totalMessageCount: (state) => {
      return state.accounts.reduce((total, account) => {
        return total + (account.messageCount || 0)
      }, 0)
    }
  },

  actions: {
    async loadAccounts() {
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.get('/api/email-accounts')
        
        if (response.data && response.data['hydra:member']) {
          this.accounts = response.data['hydra:member']
        } else {
          // Fallback avec des données de démonstration
          this.accounts = this.getDemoAccounts()
        }
        
        return { success: true }
      } catch (error) {
        console.error('Erreur lors du chargement des comptes:', error)
        this.error = 'Impossible de charger les comptes email'
        
        // Utiliser des données de démonstration en cas d'erreur
        this.accounts = this.getDemoAccounts()
        
        return { success: false, message: this.error }
      } finally {
        this.loading = false
      }
    },

    async createAccount(accountData) {
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.post('/api/email-accounts', accountData)
        
        if (response.data) {
          this.accounts.push(response.data)
          return { success: true, data: response.data }
        }
        
        return { success: false, message: 'Erreur lors de la création du compte' }
      } catch (error) {
        console.error('Erreur création compte:', error)
        const message = error.response?.data?.message || 'Erreur lors de la création du compte'
        this.error = message
        
        // Simulation pour la démo
        if (process.env.NODE_ENV === 'development') {
          const newAccount = {
            id: Date.now(),
            ...accountData,
            status: 'connected',
            messageCount: Math.floor(Math.random() * 1000),
            lastSync: new Date().toISOString(),
            createdAt: new Date().toISOString()
          }
          this.accounts.push(newAccount)
          return { success: true, data: newAccount }
        }
        
        return { success: false, message }
      } finally {
        this.loading = false
      }
    },

    async updateAccount(accountId, accountData) {
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.put(`/api/email-accounts/${accountId}`, accountData)
        
        if (response.data) {
          const index = this.accounts.findIndex(account => account.id === accountId)
          if (index !== -1) {
            this.accounts[index] = response.data
          }
          return { success: true, data: response.data }
        }
        
        return { success: false, message: 'Erreur lors de la mise à jour du compte' }
      } catch (error) {
        console.error('Erreur mise à jour compte:', error)
        const message = error.response?.data?.message || 'Erreur lors de la mise à jour du compte'
        this.error = message
        
        // Simulation pour la démo
        if (process.env.NODE_ENV === 'development') {
          const index = this.accounts.findIndex(account => account.id === accountId)
          if (index !== -1) {
            this.accounts[index] = { ...this.accounts[index], ...accountData }
            return { success: true, data: this.accounts[index] }
          }
        }
        
        return { success: false, message }
      } finally {
        this.loading = false
      }
    },

    async deleteAccount(accountId) {
      this.loading = true
      this.error = null
      
      try {
        await axios.delete(`/api/email-accounts/${accountId}`)
        
        this.accounts = this.accounts.filter(account => account.id !== accountId)
        return { success: true }
      } catch (error) {
        console.error('Erreur suppression compte:', error)
        const message = error.response?.data?.message || 'Erreur lors de la suppression du compte'
        this.error = message
        
        // Simulation pour la démo
        if (process.env.NODE_ENV === 'development') {
          this.accounts = this.accounts.filter(account => account.id !== accountId)
          return { success: true }
        }
        
        return { success: false, message }
      } finally {
        this.loading = false
      }
    },

    async testConnection(accountId) {
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.post(`/api/email-accounts/${accountId}/test`)
        
        if (response.data && response.data.success) {
          // Mettre à jour le statut du compte
          const account = this.accounts.find(acc => acc.id === accountId)
          if (account) {
            account.status = 'connected'
            account.lastSync = new Date().toISOString()
          }
          
          return { success: true, message: 'Connexion réussie' }
        }
        
        return { success: false, message: response.data?.message || 'Échec du test de connexion' }
      } catch (error) {
        console.error('Erreur test connexion:', error)
        const message = error.response?.data?.message || 'Erreur lors du test de connexion'
        this.error = message
        
        // Simulation pour la démo
        if (process.env.NODE_ENV === 'development') {
          const account = this.accounts.find(acc => acc.id === accountId)
          if (account) {
            account.status = Math.random() > 0.2 ? 'connected' : 'error'
            account.lastSync = new Date().toISOString()
          }
          return { 
            success: account?.status === 'connected', 
            message: account?.status === 'connected' ? 'Connexion réussie' : 'Échec de la connexion'
          }
        }
        
        return { success: false, message }
      } finally {
        this.loading = false
      }
    },

    async syncAccount(accountId) {
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.post(`/api/email-accounts/${accountId}/sync`)
        
        if (response.data && response.data.success) {
          // Mettre à jour les statistiques du compte
          const account = this.accounts.find(acc => acc.id === accountId)
          if (account) {
            account.status = 'connected'
            account.lastSync = new Date().toISOString()
            account.messageCount = response.data.messageCount || account.messageCount
          }
          
          return { success: true, data: response.data }
        }
        
        return { success: false, message: response.data?.message || 'Échec de la synchronisation' }
      } catch (error) {
        console.error('Erreur synchronisation:', error)
        const message = error.response?.data?.message || 'Erreur lors de la synchronisation'
        this.error = message
        
        // Simulation pour la démo
        if (process.env.NODE_ENV === 'development') {
          const account = this.accounts.find(acc => acc.id === accountId)
          if (account) {
            account.status = 'connected'
            account.lastSync = new Date().toISOString()
            account.messageCount = (account.messageCount || 0) + Math.floor(Math.random() * 10)
          }
          return { success: true, data: { messageCount: account?.messageCount } }
        }
        
        return { success: false, message }
      } finally {
        this.loading = false
      }
    },

    async startOAuthFlow(provider, email) {
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.post('/api/oauth/start', {
          provider,
          email,
          redirect_uri: `${window.location.origin}/oauth/callback`
        })
        
        if (response.data && response.data.auth_url) {
          this.oauthState = response.data.state
          return { success: true, authUrl: response.data.auth_url }
        }
        
        return { success: false, message: 'Impossible de démarrer le processus OAuth' }
      } catch (error) {
        console.error('Erreur OAuth:', error)
        const message = error.response?.data?.message || 'Erreur lors du démarrage OAuth'
        this.error = message
        
        // Simulation pour la démo
        if (process.env.NODE_ENV === 'development') {
          const authUrl = provider === 'gmail' 
            ? 'https://accounts.google.com/oauth/authorize?...'
            : 'https://login.microsoftonline.com/oauth/authorize?...'
          
          return { success: true, authUrl }
        }
        
        return { success: false, message }
      } finally {
        this.loading = false
      }
    },

    async handleOAuthCallback(code, state) {
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.post('/api/oauth/callback', {
          code,
          state
        })
        
        if (response.data && response.data.success) {
          // Recharger les comptes après succès OAuth
          await this.loadAccounts()
          return { success: true, data: response.data }
        }
        
        return { success: false, message: response.data?.message || 'Échec de l\'authentification OAuth' }
      } catch (error) {
        console.error('Erreur callback OAuth:', error)
        const message = error.response?.data?.message || 'Erreur lors du callback OAuth'
        this.error = message
        return { success: false, message }
      } finally {
        this.loading = false
      }
    },

    getDemoAccounts() {
      return [
        {
          id: 1,
          email: 'commercial@entreprise.com',
          provider: 'gmail',
          host: 'imap.gmail.com',
          port: 993,
          security: 'ssl',
          folder: 'INBOX',
          enabled: true,
          status: 'connected',
          messageCount: 1247,
          lastSync: new Date(Date.now() - 300000).toISOString(), // 5 minutes ago
          createdAt: new Date(Date.now() - 86400000).toISOString() // 1 day ago
        },
        {
          id: 2,
          email: 'support@entreprise.com',
          provider: 'outlook',
          host: 'outlook.office365.com',
          port: 993,
          security: 'ssl',
          folder: 'INBOX',
          enabled: true,
          status: 'connected',
          messageCount: 892,
          lastSync: new Date(Date.now() - 600000).toISOString(), // 10 minutes ago
          createdAt: new Date(Date.now() - 172800000).toISOString() // 2 days ago
        },
        {
          id: 3,
          email: 'commandes@fournisseur.com',
          provider: 'imap',
          host: 'mail.fournisseur.com',
          port: 993,
          security: 'ssl',
          folder: 'INBOX',
          enabled: false,
          status: 'error',
          messageCount: 0,
          lastSync: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
          createdAt: new Date(Date.now() - 259200000).toISOString() // 3 days ago
        }
      ]
    },

    clearError() {
      this.error = null
    },

    setLoading(loading) {
      this.loading = loading
    }
  }
})